import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import TextScramble from '../components/TextScramble';
import ScrollReveal from '../components/ScrollReveal';
import { User, Mail, Phone, MapPin, LogOut, Check, Package, ChevronDown, ChevronUp, Search, X, Heart, Sparkles, Gift } from 'lucide-react';
import { formatFCFA } from '../lib/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_FR = { 'Pending': 'En attente', 'Processing': 'En pr\u00e9paration', 'Shipped': 'Exp\u00e9di\u00e9e', 'Delivered': 'Livr\u00e9e', 'Cancelled': 'Annul\u00e9e' };

export default function AccountPage() {
  const { customer, loading, logout, updateProfile } = useCustomerAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', address: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [orderSearch, setOrderSearch] = useState('');
  const [loyalty, setLoyalty] = useState(null);

  useEffect(() => {
    if (!loading && !customer) navigate('/login');
  }, [loading, customer, navigate]);

  useEffect(() => {
    if (customer) {
      setForm({
        first_name: customer.first_name || '',
        last_name: customer.last_name || '',
        phone: customer.phone || '',
        address: customer.address || '',
      });
    }
  }, [customer]);

  const fetchOrders = useCallback(async () => {
    if (!customer) return;
    setOrdersLoading(true);
    try {
      const res = await axios.get(`${API}/customer/orders`, { withCredentials: true });
      setOrders(res.data);
    } catch { setOrders([]); }
    setOrdersLoading(false);
  }, [customer]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  useEffect(() => {
    if (!customer) return;
    axios.get(`${API}/customer/loyalty`, { withCredentials: true })
      .then(r => setLoyalty(r.data))
      .catch(() => setLoyalty(null));
  }, [customer]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile(form);
      setEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading || !customer) {
    return <div className="min-h-screen flex items-center justify-center"><p className="text-black/30 text-sm">Chargement...</p></div>;
  }

  return (
    <div data-testid="account-page" className="pt-24 sm:pt-28 pb-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-12">
          <TextScramble
            text="Mon Compte"
            className="text-4xl sm:text-5xl tracking-tighter font-light text-black"
            delay={0.2}
          />
          <button
            data-testid="account-logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-black/40 hover:text-black transition-colors"
          >
            <LogOut size={14} /> D&eacute;connexion
          </button>
        </div>

        {/* Loyalty widget */}
        {loyalty && (
          <div data-testid="loyalty-widget" className="mb-10 border p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" style={{ borderColor: 'rgba(184,0,0,0.15)', backgroundColor: 'rgba(184,0,0,0.03)' }}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#B80000' }}>
                <Sparkles size={20} strokeWidth={1.5} className="text-white" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/40 mb-1">Programme fidélité</p>
                <p className="font-serif text-2xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  <span data-testid="loyalty-points" style={{ color: '#B80000', fontWeight: 600 }}>{loyalty.points.toLocaleString('fr-FR')}</span>
                  <span className="text-black/60 text-base"> points</span>
                </p>
                <p className="text-xs text-black/50 mt-1">
                  Vaut {formatFCFA(loyalty.value_fcfa)} · 1 FCFA dépensé = 1 point
                </p>
              </div>
            </div>
            <div className="text-left sm:text-right">
              {loyalty.points >= loyalty.min_redeem ? (
                <p className="text-xs font-semibold" style={{ color: '#B80000' }}>
                  Vous pouvez utiliser vos points à votre prochaine commande
                </p>
              ) : (
                <p className="text-xs text-black/50">
                  Plus que <strong className="text-black/80">{(loyalty.min_redeem - loyalty.points).toLocaleString('fr-FR')}</strong> points pour débloquer une réduction
                </p>
              )}
              <div className="mt-3 w-full sm:w-48 h-1 bg-black/10 overflow-hidden">
                <div
                  className="h-full transition-all duration-500"
                  style={{ width: `${Math.min(100, (loyalty.points / loyalty.min_redeem) * 100)}%`, backgroundColor: '#B80000' }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-6 mb-10 border-b border-black/10 pb-3">
          <button
            data-testid="tab-profile"
            onClick={() => setActiveTab('profile')}
            className={`text-xs uppercase tracking-[0.2em] font-bold pb-2 border-b-2 transition-all ${activeTab === 'profile' ? 'text-black border-black' : 'text-black/30 border-transparent'}`}
          >
            Profil
          </button>
          <button
            data-testid="tab-orders"
            onClick={() => setActiveTab('orders')}
            className={`text-xs uppercase tracking-[0.2em] font-bold pb-2 border-b-2 transition-all ${activeTab === 'orders' ? 'text-black border-black' : 'text-black/30 border-transparent'}`}
          >
            Mes Commandes {orders.length > 0 && <span className="ml-1 text-[10px] px-1.5 py-0.5 text-white" style={{ backgroundColor: '#B80000' }}>{orders.length}</span>}
          </button>
          <Link
            to="/wishlist"
            data-testid="tab-wishlist-link"
            className="text-xs uppercase tracking-[0.2em] font-bold pb-2 border-b-2 border-transparent text-black/30 hover:text-black transition-all flex items-center gap-1.5"
          >
            <Heart size={12} strokeWidth={1.5} />
            Favoris
          </Link>
          <Link
            to="/referral"
            data-testid="tab-referral-link"
            className="text-xs uppercase tracking-[0.2em] font-bold pb-2 border-b-2 border-transparent text-black/30 hover:text-black transition-all flex items-center gap-1.5"
          >
            <Gift size={12} strokeWidth={1.5} />
            Parrainage
          </Link>
        </div>

        {saved && (
          <div data-testid="profile-saved" className="mb-6 p-3 flex items-center gap-2 text-sm bg-green-50 text-green-700">
            <Check size={14} /> Profil mis &agrave; jour avec succ&egrave;s
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <ScrollReveal>
            <div className="border border-black/10 p-8 sm:p-10">
              {!editing ? (
                <div className="space-y-6">
                  <div className="flex items-center gap-4 pb-6 border-b border-black/5">
                    <div className="w-16 h-16 bg-[#EDE8DE] flex items-center justify-center">
                      <User size={24} strokeWidth={1.5} className="text-black/30" />
                    </div>
                    <div>
                      <h2 className="font-serif text-2xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                        {customer.first_name} {customer.last_name}
                      </h2>
                      <p className="text-xs text-black/40 mt-1">Client depuis {new Date(customer.created_at).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Mail size={16} strokeWidth={1.5} className="text-black/30" />
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] font-bold text-black/30">Email</p>
                        <p className="text-sm mt-0.5">{customer.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone size={16} strokeWidth={1.5} className="text-black/30" />
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] font-bold text-black/30">T&eacute;l&eacute;phone</p>
                        <p className="text-sm mt-0.5">{customer.phone || 'Non renseign\u00e9'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <MapPin size={16} strokeWidth={1.5} className="text-black/30" />
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] font-bold text-black/30">Adresse</p>
                        <p className="text-sm mt-0.5">{customer.address || 'Non renseign\u00e9e'}</p>
                      </div>
                    </div>
                  </div>
                  <button data-testid="edit-profile-btn" onClick={() => setEditing(true)} className="btn-secondary mt-6">
                    Modifier le Profil
                  </button>
                </div>
              ) : (
                <form data-testid="edit-profile-form" onSubmit={handleSave} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 block mb-2">Pr&eacute;nom</label>
                      <input data-testid="edit-first-name" type="text" value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} className="bg-transparent border-b border-black/20 focus:border-black outline-none py-3 w-full text-sm transition-colors" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 block mb-2">Nom</label>
                      <input data-testid="edit-last-name" type="text" value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} className="bg-transparent border-b border-black/20 focus:border-black outline-none py-3 w-full text-sm transition-colors" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 block mb-2">T&eacute;l&eacute;phone</label>
                    <input data-testid="edit-phone" type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="bg-transparent border-b border-black/20 focus:border-black outline-none py-3 w-full text-sm transition-colors" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 block mb-2">Adresse</label>
                    <textarea data-testid="edit-address" value={form.address} onChange={e => setForm({...form, address: e.target.value})} rows={2} className="bg-transparent border-b border-black/20 focus:border-black outline-none py-3 w-full text-sm transition-colors resize-none" />
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button data-testid="save-profile-btn" type="submit" disabled={saving} className="btn-primary">{saving ? 'Sauvegarde...' : 'Sauvegarder'}</button>
                    <button type="button" onClick={() => setEditing(false)} className="btn-secondary">Annuler</button>
                  </div>
                </form>
              )}
            </div>
          </ScrollReveal>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div data-testid="orders-history">
            {/* Search bar — always visible */}
            <div className="relative mb-6" data-testid="order-search-wrapper">
              <Search size={16} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-black/30" />
              <input
                data-testid="order-search-input"
                type="text"
                value={orderSearch}
                onChange={e => setOrderSearch(e.target.value)}
                placeholder="Rechercher avec le numéro de commande (ex: TESTABC123)"
                className="w-full pl-11 pr-10 py-3 bg-[#EDE8DE]/50 border border-black/10 focus:border-black outline-none text-sm transition-colors placeholder:text-black/30"
              />
              {orderSearch && (
                <button
                  data-testid="order-search-clear"
                  onClick={() => setOrderSearch('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-black/40 hover:text-black transition-colors"
                  aria-label="Effacer la recherche"
                >
                  <X size={16} strokeWidth={1.5} />
                </button>
              )}
            </div>

            {ordersLoading ? (
              <div className="text-center py-16"><p className="text-black/30 text-sm">Chargement des commandes...</p></div>
            ) : orders.length === 0 ? (
              <div className="text-center py-16 border border-black/10 p-10" data-testid="no-orders-placeholder">
                <Package size={40} strokeWidth={1} className="text-black/15 mx-auto mb-4" />
                <p className="text-black/40 text-sm mb-6">Vous n'avez pas encore de commandes</p>
                <Link to="/shop" className="btn-primary inline-block">D&eacute;couvrir la Boutique</Link>
              </div>
            ) : (
              <>
                {(() => {
                  // Normalize: lowercase, strip spaces, '#', and any known 'ord_' / 'ord_demo_' prefix
                  const normalize = (s) => (s || '').toLowerCase().replace(/[\s#]/g, '').replace(/^ord_(demo_)?/, '');
                  const q = normalize(orderSearch);
                  const filtered = q
                    ? orders.filter(o => {
                        const nid = normalize(o.order_id);
                        const tracking = (o.tracking_number || '').toLowerCase().replace(/\s/g, '');
                        return nid.includes(q) || tracking.includes(q);
                      })
                    : orders;
                  if (filtered.length === 0) {
                    return (
                      <div data-testid="order-search-empty" className="text-center py-12 border border-black/10">
                        <p className="text-sm text-black/40">Aucune commande ne correspond à "<span className="font-semibold text-black/70">{orderSearch}</span>"</p>
                        <button
                          onClick={() => setOrderSearch('')}
                          className="mt-4 text-xs uppercase tracking-[0.2em] font-bold hover:opacity-70 transition-opacity"
                          style={{ color: '#B80000' }}
                        >
                          Voir toutes les commandes
                        </button>
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-4">
                      {q && (
                        <p className="text-xs text-black/40" data-testid="order-search-count">
                          {filtered.length} résultat{filtered.length > 1 ? 's' : ''} pour "{orderSearch}"
                        </p>
                      )}
                      {filtered.map(order => (
                  <ScrollReveal key={order.order_id}>
                    <div data-testid={`order-history-${order.order_id}`} className="border border-black/10">
                      {/* Order Header */}
                      <button
                        data-testid={`toggle-order-${order.order_id}`}
                        onClick={() => setExpandedOrder(expandedOrder === order.order_id ? null : order.order_id)}
                        className="w-full p-5 flex items-center justify-between hover:bg-[#EDE8DE] transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div>
                            <p className="text-xs text-black/30 text-left">#{(order.order_id || '').replace(/^ord_(demo_)?/, '').toUpperCase()}</p>
                            <p className="text-sm font-semibold text-left">{formatFCFA(order.total)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`text-xs px-3 py-1 ${
                            order.status === 'Delivered' ? 'bg-green-50 text-green-700' :
                            order.status === 'Shipped' ? 'bg-blue-50 text-blue-700' :
                            order.status === 'Processing' ? 'bg-yellow-50 text-yellow-700' :
                            order.status === 'Cancelled' ? 'bg-red-50 text-red-700' :
                            'bg-[#EDE8DE] text-black/50'
                          }`}>
                            {STATUS_FR[order.status] || order.status}
                          </span>
                          <span className="text-xs text-black/30">
                            {new Date(order.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                          {expandedOrder === order.order_id ? <ChevronUp size={14} className="text-black/30" /> : <ChevronDown size={14} className="text-black/30" />}
                        </div>
                      </button>

                      {/* Order Details (expanded) */}
                      {expandedOrder === order.order_id && (
                        <div className="border-t border-black/5 p-5 bg-[#EDE8DE]">
                          {order.tracking_number && (
                            <div className="mb-4 p-3 border-l-2 bg-white/40" style={{ borderColor: '#B80000' }}>
                              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/50">Numéro de suivi</p>
                              <p className="text-sm font-mono tracking-wider mt-1">{order.tracking_number}</p>
                            </div>
                          )}
                          <div className="space-y-3">
                            {order.items?.map((item, i) => (
                              <div key={i} className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-[#F5F0E8] flex-shrink-0 overflow-hidden">
                                  <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" style={{ mixBlendMode: 'multiply' }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{item.name}</p>
                                  <p className="text-xs text-black/40">Qty: {item.quantity} &times; {formatFCFA(item.price)}</p>
                                </div>
                                <p className="text-sm font-semibold">{formatFCFA(item.price * item.quantity)}</p>
                              </div>
                            ))}
                          </div>
                          <div className="mt-4 pt-4 border-t border-black/5 flex items-center justify-between">
                            <div className="text-xs text-black/30">
                              <p>Livraison: {order.shipping_address}</p>
                              {order.customer_phone && <p>T&eacute;l: {order.customer_phone}</p>}
                            </div>
                            <p className="text-base font-semibold">Total: {formatFCFA(order.total)}</p>
                          </div>
                          <div className="mt-3 flex justify-between items-center gap-3 flex-wrap">
                            <a
                              href={`${API}/customer/orders/${order.order_id}/invoice`}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`download-invoice-${order.order_id}`}
                              className="text-xs uppercase tracking-[0.2em] font-bold text-black/50 hover:text-black transition-colors"
                            >
                              ↓ Télécharger la facture
                            </a>
                            <Link
                              to={`/order/${order.order_id}`}
                              data-testid={`track-order-${order.order_id}`}
                              className="text-xs uppercase tracking-[0.2em] font-bold hover:opacity-70 transition-opacity"
                              style={{ color: '#B80000' }}
                            >
                              Suivre la commande &rarr;
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  </ScrollReveal>
                      ))}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
